import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateFamily } from '../hooks/useFamilies';
import { useIndividuals } from '../hooks/useIndividuals';
import { useDebouncedValue } from '../hooks/useDebounce';
import '../styles/FamilyCreatePage.css';

const FamilyCreatePage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    familyName: '',
    primaryContactID: '',
    primaryContactRelationship: 'Head of Household',
  });
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, 300);

  // Search the SERVER, not just the first page. The client list is capped at
  // 100 rows (backend max pageSize), so with a large caseload any client past
  // the first 100 (by last name) was previously unreachable as a primary
  // contact. Passing the search term as `searchQuery` lets the backend match
  // across ALL active clients (name / Client ID / email / phone).
  const {
    data: individualsData,
    isLoading: loadingIndividuals,
    isFetching: fetchingIndividuals,
  } = useIndividuals({
    isActive: true,
    page: 1,
    pageSize: 100,
    searchQuery: debouncedSearch || undefined,
  });

  // Mutation with optimistic updates
  const createFamilyMutation = useCreateFamily();

  const individuals = individualsData?.data || [];
  const filteredIndividuals = individuals.filter((ind) => {
    const firstName = (ind.firstName || '').toLowerCase();
    const lastName = (ind.lastName || '').toLowerCase();
    const clientID = (ind.clientID || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return `${firstName} ${lastName}`.includes(term) || clientID.includes(term);
  });

  // Gate the "no individuals found" empty state while a search is still in
  // flight (loading, background-fetching, or the debounce hasn't caught up
  // yet) so we never flash "not found" for a query that hasn't resolved.
  const isSearching =
    loadingIndividuals || fetchingIndividuals || debouncedSearch !== searchTerm;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.familyName.trim()) {
      setError('Family name is required');
      return;
    }

    if (!formData.primaryContactID) {
      setError('Please select a primary contact');
      return;
    }

    try {
      setError(null);
      const family = await createFamilyMutation.mutateAsync({
        familyName: formData.familyName.trim(),
        primaryContactID: parseInt(formData.primaryContactID),
        primaryContactRelationship: formData.primaryContactRelationship,
      });

      navigate(`/families/${family.familyID}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error creating family:', err);
    }
  };

  const loading = createFamilyMutation.isPending;

  const handleCancel = () => {
    navigate('/families');
  };

  const selectedIndividual = individuals.find((ind) => {
    return ind.individualID === parseInt(formData.primaryContactID);
  });

  return (
    <div className="family-create-page">
      <div className="page-header">
        <h1>Create New Family</h1>
        <p className="subtitle">Group individuals together for family-level services</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit} className="family-form">
        <div className="form-section">
          <h2>Family Information</h2>

          <div className="form-group">
            <label htmlFor="familyName">Family Name *</label>
            <input
              type="text"
              id="familyName"
              value={formData.familyName}
              onChange={(e) => setFormData({ ...formData, familyName: e.target.value })}
              placeholder="e.g., Smith Family, Johnson Household"
              required
            />
            <small>Choose a name to identify this family group</small>
          </div>
        </div>

        <div className="form-section">
          <h2>Primary Contact *</h2>
          <p className="section-description">
            Select an individual to be the main contact person for this family
          </p>

          <div className="form-group">
            <label htmlFor="search">Search for Individual</label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or Client ID..."
              className="search-input"
            />
          </div>

          {selectedIndividual && (
            <div className="selected-individual">
              <div className="selected-label">Selected Primary Contact:</div>
              <div className="individual-info">
                <strong>
                  {selectedIndividual.firstName} {selectedIndividual.lastName}
                </strong>
                <span className="client-id">{selectedIndividual.clientID}</span>
              </div>
            </div>
          )}

          <div className="individuals-list">
            {isSearching ? (
              <div className="empty-state">
                <p>Searching…</p>
              </div>
            ) : filteredIndividuals.length === 0 ? (
              <div className="empty-state">
                {searchTerm ? (
                  <p>No individuals found matching &quot;{searchTerm}&quot;</p>
                ) : (
                  <p>No individuals available. Please create individuals first.</p>
                )}
              </div>
            ) : (
              filteredIndividuals.map((individual) => (
                <label key={individual.individualID} className="individual-option">
                  <input
                    type="radio"
                    name="primaryContact"
                    value={individual.individualID}
                    checked={formData.primaryContactID === individual.individualID?.toString()}
                    onChange={(e) => setFormData({ ...formData, primaryContactID: e.target.value })}
                  />
                  <div className="individual-details">
                    <div className="individual-name">
                      {individual.firstName} {individual.lastName}
                    </div>
                    <div className="individual-id">{individual.clientID}</div>
                  </div>
                </label>
              ))
            )}
          </div>

          <div className="form-group">
            <label htmlFor="relationship">Primary Contact Relationship</label>
            <select
              id="relationship"
              value={formData.primaryContactRelationship}
              onChange={(e) =>
                setFormData({ ...formData, primaryContactRelationship: e.target.value })
              }
            >
              <option value="Head of Household">Head of Household</option>
              <option value="Parent">Parent</option>
              <option value="Guardian">Guardian</option>
              <option value="Spouse">Spouse</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={handleCancel} className="btn-secondary" disabled={loading}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading || !formData.familyName.trim() || !formData.primaryContactID}
          >
            {loading ? 'Creating...' : 'Create Family'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FamilyCreatePage;
